/**
 * Registration Requests Page
 * ==========================
 * صفحة مراجعة طلبات التسجيل (شركات، سائقين، عملاء) - from database
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '../../../lib/ui/Card';
import { Table } from '../../../lib/ui/Table';
import { Button } from '../../../lib/ui/Button';
import { SearchBox } from '../../../lib/ui/SearchBox';
import { Modal } from '../../../lib/ui/Modal';
import { Dropdown } from '../../../lib/ui/Dropdown';
import { registrationRequestsService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useDialog } from '../../../context/DialogContext';
import './RegistrationRequestsPage.css';

const FILE_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1').replace('/api/v1', '');

function isFileUrl(v) {
  return v && (v.startsWith('http') || v.startsWith('/uploads'));
}
function fullUrl(v) {
  return v.startsWith('/') ? `${FILE_BASE_URL}${v}` : v;
}
const IMG_EXTS = /\.(jpe?g|png|webp|gif|bmp|svg)$/i;
function FileCell({ value }) {
  const href = fullUrl(value);
  const isImg = IMG_EXTS.test(value);
  if (isImg) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="watrly-regreq__img-preview">
        <img src={href} alt="" />
        <span className="watrly-regreq__img-overlay">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{width:16,height:16}}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
        </span>
      </a>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="watrly-regreq__file-link">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{width:13,height:13}}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
      عرض الملف
    </a>
  );
}

const Icons = {
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  building: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1"/><path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16"/></svg>,
  truck: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
  user: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
};

const RegistrationRequestsPage = () => {
  const toast = useToast();
  const { confirm } = useDialog();

  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [requestsData, statsData] = await Promise.all([
        registrationRequestsService.getAll().catch(() => ({ items: [] })),
        registrationRequestsService.getStats().catch(() => ({})),
      ]);

      setRequests(requestsData.items || []);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('فشل في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (request) => {
    setProcessing(true);
    try {
      await registrationRequestsService.approve(request.id);
      toast.success('تمت الموافقة على الطلب بنجاح');
      setShowDetailModal(false);
      loadData();
    } catch (error) {
      toast.error('فشل في الموافقة على الطلب');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    setProcessing(true);
    try {
      await registrationRequestsService.reject(selectedRequest.id, rejectReason);
      toast.success('تم رفض الطلب');
      setShowRejectModal(false);
      setShowDetailModal(false);
      setRejectReason('');
      loadData();
    } catch (error) {
      toast.error('فشل في رفض الطلب');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (request) => {
    if (!await confirm({ title: 'حذف الطلب', message: 'هل أنت متأكد من حذف هذا الطلب؟', type: 'delete', confirmText: 'حذف', cancelText: 'تراجع' })) return;
    try {
      await registrationRequestsService.delete(request.id);
      toast.success('تم حذف الطلب');
      loadData();
    } catch (error) {
      toast.error('فشل في حذف الطلب');
    }
  };

  const getTypeLabel = (type) => {
    const labels = { tenant: 'شركة', driver: 'سائق', customer: 'عميل' };
    return labels[type] || type;
  };

  const getTypeIcon = (type) => {
    const map = {
      tenant:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{width:14,height:14}}><path d="M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1"/><path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16"/></svg>,
      driver:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{width:14,height:14}}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/><line x1="12" y1="2" x2="12" y2="9"/><line x1="12" y1="15" x2="12" y2="22"/><line x1="2" y1="12" x2="9" y2="12"/><line x1="15" y1="12" x2="22" y2="12"/></svg>,
      customer: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{width:14,height:14}}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
    };
    return map[type] || null;
  };

  const getStatusLabel = (status) => {
    const labels = { pending: 'قيد المراجعة', approved: 'تمت الموافقة', rejected: 'مرفوض' };
    return labels[status] || status;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ar-SA', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const filteredRequests = useMemo(() => {
    return requests.filter(r => {
      const matchesSearch = 
        r.applicant_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.applicant_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.company_name?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = typeFilter === 'all' || r.request_type === typeFilter;
      const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [requests, searchQuery, typeFilter, statusFilter]);

  const typeOptions = [
    { value: 'all', label: 'جميع الأنواع' },
    { value: 'tenant', label: 'شركات' },
    { value: 'driver', label: 'سائقين' },
    { value: 'customer', label: 'عملاء' },
  ];

  const statusOptions = [
    { value: 'all', label: 'جميع الحالات' },
    { value: 'pending', label: 'قيد المراجعة' },
    { value: 'approved', label: 'تمت الموافقة' },
    { value: 'rejected', label: 'مرفوض' },
  ];

  const columns = [
    { 
      key: 'request_type', 
      title: 'النوع', 
      render: (val) => (
        <span className={`watrly-regreq__type watrly-regreq__type--${val}`}>
          {getTypeIcon(val)}
          {getTypeLabel(val)}
        </span>
      )
    },
    { 
      key: 'applicant_name', 
      title: 'مقدم الطلب', 
      render: (val, row) => (
        <div className="watrly-regreq__applicant">
          <span className="watrly-regreq__applicant-name">{val}</span>
          <span className="watrly-regreq__applicant-email">{row.applicant_email}</span>
        </div>
      )
    },
    { key: 'company_name', title: 'اسم الشركة', render: (val) => val || '-' },
    { key: 'created_at', title: 'تاريخ الطلب', render: (val) => formatDate(val) },
    { 
      key: 'status', 
      title: 'الحالة', 
      render: (val) => (
        <span className={`watrly-regreq__status watrly-regreq__status--${val}`}>
          {getStatusLabel(val)}
        </span>
      )
    },
    { 
      key: 'actions', 
      title: '', 
      sortable: false, 
      render: (_, row) => (
        <div className="watrly-regreq__actions">
          <button 
            className="watrly-regreq__action-btn watrly-regreq__action-btn--view"
            onClick={() => { setSelectedRequest(row); setShowDetailModal(true); }}
            title="عرض التفاصيل"
          >
            {Icons.eye}
          </button>
          {row.status === 'pending' && (
            <>
              <button 
                className="watrly-regreq__action-btn watrly-regreq__action-btn--approve"
                onClick={() => handleApprove(row)}
                title="موافقة"
              >
                {Icons.check}
              </button>
              <button 
                className="watrly-regreq__action-btn watrly-regreq__action-btn--reject"
                onClick={() => { setSelectedRequest(row); setShowRejectModal(true); }}
                title="رفض"
              >
                {Icons.x}
              </button>
            </>
          )}
          <button 
            className="watrly-regreq__action-btn watrly-regreq__action-btn--delete"
            onClick={() => handleDelete(row)}
            title="حذف"
          >
            {Icons.trash}
          </button>
        </div>
      )
    },
  ];

  return (
    <div className="watrly-regreq">
      {/* Header */}
      <div className="watrly-regreq__header">
        <div>
          <h1 className="watrly-regreq__title">طلبات التسجيل</h1>
          <p className="watrly-regreq__subtitle">
            مراجعة وإدارة طلبات التسجيل للشركات والسائقين والعملاء • البيانات من قاعدة البيانات
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="watrly-regreq__stats">
        <div className="watrly-regreq__stat-card watrly-regreq__stat-card--pending">
          <div className="watrly-regreq__stat-icon">{Icons.clock}</div>
          <div className="watrly-regreq__stat-content">
            <span className="watrly-regreq__stat-value">{stats.pending || 0}</span>
            <span className="watrly-regreq__stat-label">قيد المراجعة</span>
          </div>
        </div>
        <div className="watrly-regreq__stat-card watrly-regreq__stat-card--approved">
          <div className="watrly-regreq__stat-icon">{Icons.check}</div>
          <div className="watrly-regreq__stat-content">
            <span className="watrly-regreq__stat-value">{stats.approved || 0}</span>
            <span className="watrly-regreq__stat-label">تمت الموافقة</span>
          </div>
        </div>
        <div className="watrly-regreq__stat-card watrly-regreq__stat-card--rejected">
          <div className="watrly-regreq__stat-icon">{Icons.x}</div>
          <div className="watrly-regreq__stat-content">
            <span className="watrly-regreq__stat-value">{stats.rejected || 0}</span>
            <span className="watrly-regreq__stat-label">مرفوض</span>
          </div>
        </div>
        <div className="watrly-regreq__stat-card">
          <div className="watrly-regreq__stat-icon">{Icons.inbox}</div>
          <div className="watrly-regreq__stat-content">
            <span className="watrly-regreq__stat-value">{stats.total || 0}</span>
            <span className="watrly-regreq__stat-label">إجمالي الطلبات</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card padding="md" className="watrly-regreq__filters-card">
        <div className="watrly-regreq__filters">
          <SearchBox
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="بحث عن طلب..."
            className="watrly-regreq__search"
          />
          <div className="watrly-regreq__filter-group">
            <Dropdown
              options={typeOptions}
              value={typeFilter}
              onChange={setTypeFilter}
              placeholder="النوع"
            />
            <Dropdown
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="الحالة"
            />
          </div>
        </div>
      </Card>

      {/* Results */}
      <div className="watrly-regreq__results-info">
        عرض {filteredRequests.length} من {requests.length} طلب
      </div>

      {/* Table */}
      <Card padding="none" className="watrly-regreq__table-card">
        <Table
          columns={columns}
          data={filteredRequests}
          loading={loading}
          emptyMessage="لا يوجد طلبات تسجيل حالياً"
          hoverable
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="تفاصيل طلب التسجيل"
        size="lg"
        footer={
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setShowDetailModal(false)}>إغلاق</Button>
            {selectedRequest?.status === 'pending' && (
              <>
                <Button variant="danger" onClick={() => setShowRejectModal(true)}>رفض</Button>
                <Button variant="success" onClick={() => handleApprove(selectedRequest)} loading={processing}>موافقة</Button>
              </>
            )}
          </div>
        }
      >
        {selectedRequest && (
          <div className="watrly-regreq__detail">

            {/* ── Type + Status header ── */}
            <div className="watrly-regreq__detail-header">
              <div className={`watrly-regreq__detail-type-badge watrly-regreq__detail-type-badge--${selectedRequest.request_type}`}>
                {getTypeIcon(selectedRequest.request_type)}
                {getTypeLabel(selectedRequest.request_type)}
              </div>
              <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
                <span className={`watrly-regreq__status watrly-regreq__status--${selectedRequest.status}`}>
                  {getStatusLabel(selectedRequest.status)}
                </span>
                <span className="watrly-regreq__detail-date">{formatDate(selectedRequest.created_at)}</span>
              </div>
            </div>

            {/* ── Basic info ── */}
            <div className="watrly-regreq__detail-section">
              <div className="watrly-regreq__detail-section-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{width:14,height:14}}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                بيانات مقدم الطلب
              </div>
              <DetailRow label="الاسم الكامل" value={selectedRequest.applicant_name} />
              <DetailRow label="الجوال"       value={selectedRequest.applicant_phone} copyable />
              <DetailRow label="البريد"       value={selectedRequest.applicant_email} copyable />
              {selectedRequest.company_name && (
                <DetailRow label="اسم الشركة" value={selectedRequest.company_name} />
              )}
            </div>

            {/* ── request_data ── */}
            {selectedRequest.request_data && (
              <RequestDataSection data={selectedRequest.request_data} type={selectedRequest.request_type} />
            )}

            {/* ── Notes / reject reason ── */}
            {selectedRequest.notes && (
              <div className="watrly-regreq__detail-section">
                <div className="watrly-regreq__detail-section-title">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{width:14,height:14}}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  ملاحظات المراجع
                </div>
                <p style={{margin:0,color:'#475569',fontSize:'0.875rem',lineHeight:1.6}}>{selectedRequest.notes}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => { setShowRejectModal(false); setRejectReason(''); }}
        title="رفض الطلب"
        size="sm"
        footer={
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => { setShowRejectModal(false); setRejectReason(''); }}>إلغاء</Button>
            <Button variant="danger" onClick={handleReject} loading={processing}>رفض</Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <p>هل أنت متأكد من رفض طلب <strong>{selectedRequest?.applicant_name}</strong>؟</p>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="سبب الرفض (اختياري)..."
            style={{ 
              width: '100%', 
              minHeight: '80px', 
              padding: '0.75rem', 
              border: '1px solid #E5E5E5', 
              borderRadius: '0.5rem',
              fontSize: '0.875rem'
            }}
          />
        </div>
      </Modal>
    </div>
  );
};

/* ═══════════════ Helper components ═══════════════ */

function DetailRow({ label, value, copyable }) {
  if (!value) return null;
  return (
    <div className="watrly-regreq__detail-row">
      <span className="watrly-regreq__detail-label">{label}</span>
      <span className="watrly-regreq__detail-value" dir="ltr">
        {value}
        {copyable && (
          <button
            onClick={() => navigator.clipboard?.writeText(value)}
            title="نسخ"
            style={{background:'none',border:'none',cursor:'pointer',color:'#94a3b8',marginRight:4,padding:'2px 4px'}}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{width:13,height:13,verticalAlign:'middle'}}><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          </button>
        )}
      </span>
    </div>
  );
}

const FIELD_LABELS = {
  // ─── Tenant (Company) ───────────────────────────────────────
  commercial_registration_no:     'رقم السجل التجاري',
  commercial_registration_expiry: 'تاريخ انتهاء السجل',
  operating_license_no:           'رقم رخصة التشغيل',
  operating_license_expiry:       'تاريخ انتهاء الرخصة',
  issuing_authority:              'الجهة المُصدِرة',
  vat_registration_no:            'رقم التسجيل الضريبي (VAT)',
  website:                        'الموقع الإلكتروني',
  notes:                          'ملاحظات',
  city:                           'المدينة',
  region:                         'المنطقة الإدارية',
  water_types:                    'أنواع المياه',
  company_name_en:                'اسم الشركة (إنجليزي)',
  owner_name:                     'اسم المسؤول',
  owner_phone:                    'جوال المسؤول',
  // ─── Driver — basic ─────────────────────────────────────────
  national_id:                    'رقم الهوية / الإقامة',
  ship_code:                      'كود السفينة',
  ship_name:                      'اسم السفينة',
  // ─── Driver — Identity ──────────────────────────────────────
  driver_full_name:               'الاسم الكامل (هوية)',
  id_type:                        'نوع الهوية',
  id_number:                      'رقم الهوية / الإقامة',
  id_expiry:                      'تاريخ انتهاء الهوية',
  nationality:                    'الجنسية',
  date_of_birth:                  'تاريخ الميلاد',
  personal_photo:                 'صورة شخصية',
  id_photo_front:                 'صورة الهوية (وجه)',
  id_photo_back:                  'صورة الهوية (خلف)',
  // ─── Driver — Driving License ───────────────────────────────
  driving_license_number:         'رقم رخصة القيادة',
  driving_license_type:           'نوع رخصة القيادة',
  driving_license_expiry:         'تاريخ انتهاء الرخصة',
  driving_license_front:          'صورة الرخصة (وجه)',
  driving_license_back:           'صورة الرخصة (خلف)',
  // ─── Driver — Health ────────────────────────────────────────
  medical_exam_date:              'تاريخ آخر فحص طبي',
  medical_exam_file:              'ملف الفحص الطبي',
  health_certificate_number:      'رقم الشهادة الصحية',
  health_certificate_file:        'ملف الشهادة الصحية',
  drug_test_result:               'نتيجة فحص المخدرات',
  // ─── Driver — Contact ───────────────────────────────────────
  driver_phone:                   'رقم الجوال',
  driver_phone_2:                 'رقم جوال إضافي',
  emergency_contact_name:         'اسم شخص للطوارئ',
  emergency_contact_phone:        'هاتف الطوارئ',
  emergency_contact_relation:     'صلة القرابة',
  driver_address:                 'العنوان',
  // ─── Driver — Financial ─────────────────────────────────────
  driver_bank_name:               'اسم البنك',
  driver_iban:                    'رقم الآيبان (IBAN)',
  // ─── Driver — Vehicle ───────────────────────────────────────
  vehicle_plate:                  'رقم لوحة المركبة',
  vehicle_plate_number:           'رقم اللوحة',
  vehicle_type:                   'نوع المركبة',
  vehicle_make:                   'شركة صنع المركبة',
  vehicle_model:                  'موديل المركبة',
  vehicle_year:                   'سنة الصنع',
  vehicle_color:                  'لون المركبة',
  vehicle_registration_no:        'رقم تسجيل المركبة',
  vehicle_registration_expiry:    'تاريخ انتهاء تسجيل المركبة',
  vehicle_registration_file:      'ملف استمارة التسجيل',
  vehicle_insurance_no:           'رقم وثيقة التأمين',
  vehicle_insurance_expiry:       'تاريخ انتهاء التأمين',
  vehicle_insurance_file:         'ملف وثيقة التأمين',
  vehicle_capacity:               'طاقة التحميل (ل)',
  vehicle_photo:                  'صورة المركبة',
  // ─── Direct drivers table columns (fetched by backend enrichment) ─────────
  license_number:                 'رقم رخصة القيادة',
  license_expiry:                 'تاريخ انتهاء الرخصة',
  license_type:                   'نوع الرخصة',
  iqama_number:                   'رقم الإقامة',
  iqama_expiry:                   'تاريخ انتهاء الإقامة',
  health_certificate_expiry:      'تاريخ انتهاء الشهادة الصحية',
  bank_name:                      'اسم البنك',
  iban:                           'رقم الآيبان (IBAN)',
  address:                        'العنوان',
  // ─── Skip ────────────────────────────────────────────────────────────────
  full_name:                      null,
  phone:                          null,
  driver_id:                      null,
  tenant_id:                      null,
  user_id:                        null,
};


const WATER_LABELS = { drinking:'مياه شرب', non_drinking:'مياه غير صالحة للشرب', treated:'مياه معالجة' };
const SKIP_KEYS = new Set(['tenant_id', 'user_id', 'driver_id', 'full_name', 'phone', 'requirements_data']);

const NATIONALITY_LABELS = { sa:'سعودي', eg:'مصري', ye:'يمني', pk:'باكستاني', in:'هندي', bd:'بنغلاديشي', sy:'سوري', jo:'أردني', ps:'فلسطيني', sd:'سوداني', et:'إثيوبي', id:'إندونيسي', ph:'فلبيني', ng:'نيجيري', ma:'مغربي', iq:'عراقي', lb:'لبناني', tn:'تونسي', er:'إريتري', so:'صومالي', other:'أخرى' };
const BANK_LABELS = { snb:'البنك الأهلي السعودي (SNB)', rajhi:'بنك الراجحي', riyad:'بنك الرياض', aljazirah:'بنك الجزيرة', fransi:'البنك السعودي الفرنسي', anb:'البنك العربي الوطني', albilad:'بنك البلاد', saib:'البنك السعودي للاستثمار', alinma:'مصرف الإنماء', bsf:'البنك السعودي الفرنسي', enbd:'بنك الإمارات دبي الوطني', gulf:'بنك الخليج الدولي', other:'بنك آخر' };
const ID_TYPE_LABELS = { national_id:'هوية وطنية', iqama:'إقامة', passport:'جواز سفر' };
const LICENSE_TYPE_LABELS = { public_transport:'نقل عام', heavy_vehicle:'مركبات ثقيلة', private:'خاصة' };
const RELATION_LABELS = { father:'أب', mother:'أم', brother:'أخ', spouse:'زوج/زوجة', son:'ابن', friend:'صديق', other:'أخرى' };
const SELECT_MAPS = { nationality: NATIONALITY_LABELS, driver_bank_name: BANK_LABELS, bank_name: BANK_LABELS, id_type: ID_TYPE_LABELS, driving_license_type: LICENSE_TYPE_LABELS, license_type: LICENSE_TYPE_LABELS, emergency_contact_relation: RELATION_LABELS };

function formatFieldValue(key, val) {
  if (val === null || val === undefined || val === '') return null;
  if (key === 'water_types' && Array.isArray(val))
    return val.map(k => WATER_LABELS[k] || k).join('، ') || null;
  if (Array.isArray(val)) return val.join('، ');
  if (typeof val === 'object') return JSON.stringify(val);
  const str = String(val);
  // Resolve select dropdown codes to Arabic labels
  if (SELECT_MAPS[key] && SELECT_MAPS[key][str]) return SELECT_MAPS[key][str];
  return str;
}

function RequestDataSection({ data, type }) {
  // ── Main fields (non-requirements_data) ──
  const mainRows = Object.entries(data)
    .filter(([k]) => !SKIP_KEYS.has(k) && FIELD_LABELS[k] !== null)
    .map(([k, v]) => ({ key: k, label: FIELD_LABELS[k] || k, value: formatFieldValue(k, v) }))
    .filter(r => r.value);

  // ── requirements_data (dynamic fields from registration) ──
  const reqData = data.requirements_data || {};
  const reqRows = Object.entries(reqData)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => ({ key: k, label: FIELD_LABELS[k] || k.replace(/_/g,' '), value: formatFieldValue(k, v) }))
    .filter(r => r.value);

  if (!mainRows.length && !reqRows.length) return null;

  return (
    <>
      {mainRows.length > 0 && (
        <div className="watrly-regreq__detail-section">
          <div className="watrly-regreq__detail-section-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{width:14,height:14}}>
              <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
            </svg>
            {type === 'tenant' ? 'بيانات الشركة' : 'بيانات إضافية'}
          </div>
          {mainRows.map(({ key, label, value }) => (
            <div key={key} className="watrly-regreq__detail-row">
              <span className="watrly-regreq__detail-label">{label}</span>
              <span className="watrly-regreq__detail-value" dir={key === 'water_types' ? 'rtl' : 'ltr'}>{value}</span>
            </div>
          ))}
        </div>
      )}

      {reqRows.length > 0 && (
        <div className="watrly-regreq__detail-section">
          <div className="watrly-regreq__detail-section-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{width:14,height:14}}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
            </svg>
            الوثائق والمستندات
          </div>
          {reqRows.map(({ key, label, value }) => (
            <div key={key} className={`watrly-regreq__detail-row${isFileUrl(value) && IMG_EXTS.test(value) ? ' watrly-regreq__detail-row--file' : ''}`}>
              <span className="watrly-regreq__detail-label">{label}</span>
              {isFileUrl(value)
                ? <FileCell value={value} />
                : <span className="watrly-regreq__detail-value" dir="ltr">{value}</span>}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default RegistrationRequestsPage;
